import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { AuthLayout } from "#app/components/auth/auth-layout";
import { Box, Button, Icon, Text } from "@salestrak/ui";

/** The mail glyph the check-your-email screens center on. It sits inside the
 *  form card, so the tile is a `sunken` step with a hairline: a `muted` fill is
 *  within 4 L of the card and reads as a smudge, not a shape. */
export function MailCircle() {
  return (
    <Box direction="row" justify="center">
      <Box
        width={56}
        height={56}
        rounded="xl"
        background="sunken"
        border
        borderColor="border"
        direction="column"
        align="center"
        justify="center"
      >
        <Icon name="mail" size="xl" color="muted" />
      </Box>
    </Box>
  );
}

type EmailSentScreenProps = {
  /** Names what was sent and to whom, e.g. "We've sent a verification link to …". */
  subheading: string;
  /** Re-send the email. `undefined` (no address to send to) disables the affordance. */
  onResend: (() => Promise<void>) | undefined;
};

/** How long the resend affordance rests after a send, so a double press or
 *  an impatient run of presses does not mail the same link again and again. */
export const RESEND_COOLDOWN_MS = 30_000;

type ResendState = "idle" | "sending" | "sent" | "failed";

/**
 * The "Check Your Email" screen: mail glyph, back-to-sign-in, and a resend
 * affordance. Shared by the verification-sent and reset-password-sent routes,
 * which differ only in copy and which resend endpoint they call.
 */
export function EmailSentScreen({ subheading, onResend }: EmailSentScreenProps) {
  const navigate = useNavigate();
  const [resend, setResend] = React.useState<ResendState>("idle");

  // A send confirms itself, then the affordance comes back after the cooldown.
  React.useEffect(() => {
    if (resend !== "sent") return;
    const timer = setTimeout(() => setResend("idle"), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [resend]);

  const handleResend = async () => {
    if (!onResend || resend === "sending" || resend === "sent") return;
    setResend("sending");
    try {
      await onResend();
      setResend("sent");
    } catch {
      setResend("failed");
    }
  };

  return (
    <AuthLayout heading="Check Your Email" subheading={subheading}>
      <Box direction="column" gap="lg">
        <MailCircle />

        <Box direction="column" paddingTop="lg">
          <Button size="xl" onClick={() => navigate({ to: "/login" })}>
            Back to login
          </Button>
        </Box>

        {onResend && resend === "sent" ? (
          <Text size="sm" variant="muted" align="center" role="status">
            Sent. Check your inbox for the new link.
          </Text>
        ) : onResend ? (
          <Box direction="column" align="center" gap="sm">
            <Box
              as="button"
              type="button"
              onClick={resend === "sending" ? undefined : () => void handleResend()}
              cursor="pointer"
            >
              <Text size="sm" align="center">
                <Text size="sm" variant="muted">
                  Didn't receive it?{" "}
                </Text>
                <Text size="sm" weight="semibold">
                  {resend === "sending" ? "Resending..." : "Resend"}
                </Text>
              </Text>
            </Box>
            {resend === "failed" ? (
              <Text size="xs" weight="medium" variant="destructive" role="alert">
                The email could not be sent. Try again in a moment.
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </AuthLayout>
  );
}
